import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Select, Store} from '@ngxs/store';
import {InventoryState} from '../../../../shared/states/inventory/inventory.state';
import {Observable} from 'rxjs/internal/Observable';
import {GetInventoryList} from '../../../../shared/states/inventory/inventory.actions';
import {InventoryTablesEnum} from '../../../interfaces/InventoryTablesEnum';
import {SearchInputColorEnum} from '../../../../shared/components/search-input/search-input.component';
import {IInventory} from '../../../../shared/interfaces/inventory.interface';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {InventoryInfoRequest} from 'src/app/shared/interfaces/search-criteria';
import {IAlertMessage} from '../../../../shared/interfaces/alert-message.interface';
import {isSubstringOfElement, sortByProperty} from '../../../../shared/helpers/helpers';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { AlertTypesEnum } from 'src/app/shared/interfaces/AlertTypesEnum';
import { pipe } from 'rxjs';
import Decimal from 'decimal.js';

@UntilDestroy()
@Component({
  selector: 'app-inventory-page',
  templateUrl: './inventory-page.component.html',
  styleUrls: ['./inventory-page.component.scss', './../../../inventory.scss'],
})
export class InventoryPageComponent implements OnInit {
  inventoryControlsForm: UntypedFormGroup;

  currentTab: InventoryTablesEnum = InventoryTablesEnum.Active;

  InventoryTablesEnum = InventoryTablesEnum;
  SearchInputColorEnum = SearchInputColorEnum;

  uniqueNDCsNumber = 0;

  inventoryActiveList: IInventory[] = [];
  inventoryArchivedList: IInventory[] = [];

  inventoryActiveListFiltered: IInventory[] = [];
  inventoryArchivedListFiltered: IInventory[] = [];

  @Select(InventoryState.inventoryList) inventoryList$: Observable<IInventory[]>;
  @Select(InventoryState.inventoryActiveList) inventoryActiveList$: Observable<IInventory[]>;
  @Select(InventoryState.inventoryArchivedList) inventoryArchivedList$: Observable<IInventory[]>;
  @Select(InventoryState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  alertMessage: IAlertMessage;
  autohideDelay = 3;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private route: ActivatedRoute,
    private notificationService: AlertMessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.inventoryControlsForm = this.fb.group({
      searchValue: ['', []],
      schedules: null,
      inventoryLocations: null
    });
  }

  ngOnInit(): void {
    this.getTableData();
    const bulkAddedNumber = Number(this.route.snapshot.queryParams.bulkAdded);

    if (bulkAddedNumber) {
      setTimeout(() => {
        this.notificationService.displayAlert(`Successfully added ${bulkAddedNumber} NDC${bulkAddedNumber !== 1 ? 's' : ''} to inventory.`, AlertTypesEnum.Success);
      });
    }

    this.inventoryActiveList$.pipe(untilDestroyed(this))
      .subscribe((inventories: IInventory[]) => {
      this.inventoryActiveList = inventories && inventories.length ? inventories : [];
        
      this.inventoryActiveList = inventories?.map(inventory => ({
        ...inventory,
        packageNdcs: inventory.packageNdcs.map(ndc => ndc.replace("\n","").trim().replace(/-/g, ''))
      }))
       this.applyFilters();
      });

    this.inventoryArchivedList$.pipe(untilDestroyed(this))
      .subscribe((inventories: IInventory[]) => {
        this.inventoryArchivedList = inventories && inventories.length ? inventories : [];
        this.inventoryArchivedList = inventories?.map(inventory => ({
          ...inventory,
          packageNdcs: inventory.packageNdcs.map(ndc => ndc.replace("\n","").trim().replace(/-/g, ''))
        }))
        this.inventoryArchivedListFiltered = this.inventoryArchivedList ? [...this.inventoryArchivedList] : [];
      });

    this.inventoryList$.pipe(untilDestroyed(this))
      .subscribe((response: any[]) => {
        if (response && response.length) {
          this.uniqueNDCsNumber = [...new Set(response.map(el => el.packageNdc))]?.length;
        }
      });

      this.inventoryControlsForm.get('schedules').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.getTableData());
      
      this.inventoryControlsForm.get('inventoryLocations')
      .valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(locations => {
        this.applyFilters();
      });

      this.inventoryControlsForm.get('searchValue')
      .valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(locations => {
        this.applyFilters();
        this.cdr.detectChanges();
      });

  }

  applyFilters(): void {
    //reset prev filtered before each filtering
    this.inventoryActiveListFiltered =  this.inventoryActiveList;
    const formValue = this.inventoryControlsForm.getRawValue();
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.filterByLocations(formValue.inventoryLocations?.map(x => x?.id));
    }
    this.searchFilter(formValue.searchValue, this.inventoryActiveListFiltered);
  }

  filterByLocations(locationIds: string[]): void {
    if (!locationIds?.length) {
      if (this.currentTab === InventoryTablesEnum.Active) {
        this.inventoryActiveListFiltered = this.inventoryActiveList;
      } else this.inventoryArchivedListFiltered = this.inventoryArchivedList;
    }
    else {
      function filterFunction(list: IInventory[]): IInventory[] {
       return list.filter(inventory => inventory?.locations?.some(loc => locationIds.includes(loc.locationID)));
      }

      if (this.currentTab === InventoryTablesEnum.Active) {
        this.inventoryActiveListFiltered = filterFunction(this.inventoryActiveList);
      } else {
        this.inventoryArchivedListFiltered = filterFunction(this.inventoryArchivedList);
      }
    }
    this.inventoryActiveListFiltered = this.updateOnHandForLocations(this.inventoryActiveListFiltered, locationIds);
  }

  private updateOnHandForLocations (inventoryList: IInventory[], locationIDs: string[]): IInventory[] {
    return inventoryList?.map(inventory => {
      const aggregatedOnHand = inventory.locations.reduce((total, location) => {
        if (!locationIDs || !locationIDs.length || locationIDs.includes(location.locationID)) {
          return new Decimal(+total).add(new Decimal(+location.onHand)).toNumber();
        }
        return total;
      }, 0);
      return { ...inventory, onHand: aggregatedOnHand };
    });
  };

  searchFilter(value: string, source: IInventory[] = this.inventoryActiveList): void {
    value = value?.replace(/-/g, '').trim();
    if (this.currentTab === InventoryTablesEnum.Active) {
      this.inventoryActiveListFiltered = value ?
        source?.filter(el => {
          return (
            el.friendlyName.toLowerCase().includes(value.toLowerCase())) ||
            (el.packageNdcs.find(el => isSubstringOfElement(el, value))) ||
            el?.gpi?.includes(value)
        }) :
        sortByProperty([...source || []], 'lastActivity.createDate', false);
    } else if (this.currentTab === InventoryTablesEnum.Archived) {
      this.inventoryArchivedListFiltered = value ?
        this.inventoryArchivedList.filter(el => (el.friendlyName.toLowerCase().includes(value.toLowerCase())) || 
        (el.packageNdcs.find(el => isSubstringOfElement(el, value)))) :
        sortByProperty([...this.inventoryArchivedList], 'lastActivity.createDate', false);
    }
  }

  getTableData(): void {
    const raw = this.inventoryControlsForm.getRawValue();
    const criteria = new InventoryInfoRequest();
    criteria.active = null;
    criteria.withLastAudit = true;
    criteria.schedules = raw?.schedules?.map(x => x.id);
    this.store.dispatch(new GetInventoryList(criteria));
  }

  tabChanged(tab: InventoryTablesEnum): void {
    this.inventoryControlsForm.get('searchValue').patchValue('');
    this.searchFilter('');
    this.currentTab = tab;
  }

  afterNDCArchived(response, archive): void {
    let text: string;
    let type: AlertTypesEnum;
    if (response) {
      this.getTableData();
      if (archive) {
        text = 'Successfully archived NDC ' + response.inventory.packageNdc;
        type = AlertTypesEnum.Success;
      } else {
        text = 'Successfully un-archived NDC ' + response.inventory.packageNdc;
        type = AlertTypesEnum.Success;
      }
    } else {
      text = 'Unable to process NDC ' + response.inventory.packageNdc;
      type = AlertTypesEnum.Error;
    }
    this.notificationService.displayAlert(text, type);
  }
}
